import React, { useState, useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Form, FormGroup, Label, Input, Row, Col, Alert } from "reactstrap";
import Loading from "../components/Loading";
import LocationPicker from "../components/LocationPicker";
import { getOrganisation, updateOrganisation } from "../utils/apiService";
import { canEdit } from "../utils/orgRoles";
import { BUSINESS_TYPES, MONETARY_CURRENCIES } from "../utils/orgConstants";

const OrganisationEditComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [form, setForm] = useState({
    orgName: "",
    orgIdentifier: "",
    businessType: 1,
    monetaryCurrency: 840,
    latitude: 0,
    longitude: 0,
  });
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [locationErrors, setLocationErrors] = useState({});

  useEffect(() => {
    loadOrganisation();
  }, [id]);

  const loadOrganisation = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const data = await getOrganisation(id, token);

      const role = data?.userRole || data?.role || null;
      setUserRole(role);

      if (!canEdit(role)) {
        history.push(`/organisations/${id}`);
        return;
      }

      setForm({
        orgName: data?.name || data?.orgName || "",
        orgIdentifier: data?.identifier || data?.orgIdentifier || "",
        businessType: data?.businessType || 1,
        monetaryCurrency: data?.monetaryCurrency || 840,
        latitude: data?.latitude || 0,
        longitude: data?.longitude || 0,
      });
    } catch (err) {
      setError(err?.message || "Failed to load organisation");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'businessType' || name === 'monetaryCurrency' ? parseInt(value, 10) : value
    }));
  };

  const onLocationChange = ({ latitude, longitude }) => {
    setForm((prev) => ({ ...prev, latitude, longitude }));
    setLocationErrors({});
  };

  const validate = () => {
    const errors = {};

    if (!form.orgName || form.orgName.trim() === '') {
      errors.orgName = 'Organisation name is required';
    }

    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.latitude = 'Latitude must be between -90 and 90';
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      errors.longitude = 'Longitude must be between -180 and 180';
    }

    if (!form.businessType) {
      errors.businessType = 'Business type is required';
    }

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLocationErrors({});

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      if (errors.latitude || errors.longitude) {
        setLocationErrors({ latitude: errors.latitude, longitude: errors.longitude });
      }
      setError(errors.orgName || errors.businessType || 'Please fix the validation errors');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);

      // Build payload conforming to DC_Update_Org
      const payload = {
        orgId: id,
        orgName: form.orgName || null,
        businessType: form.businessType,
        monetaryCurrency: form.monetaryCurrency,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        orgIdentifier: form.orgIdentifier || null,
      };

      await updateOrganisation(payload, token);
      history.push(`/organisations/${id}`);
    } catch (err) {
      setError(err?.message || "Failed to update organisation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!canEdit(userRole)) {
    return (
      <Alert color="warning">
        You do not have permission to edit this organisation.
        <div className="mt-3">
          <Button color="secondary" onClick={() => history.push(`/organisations/${id}`)}>
            Back to Details
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <h1 className="mb-0">Edit Organisation</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert color="danger">{error}</Alert>
          )}
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="orgName">
                    Organisation Name <span className="text-danger">*</span>
                  </Label>
                  <Input id="orgName" name="orgName" value={form.orgName} onChange={onChange} required />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="orgIdentifier">Organisation Identifier</Label>
                  <Input id="orgIdentifier" name="orgIdentifier" value={form.orgIdentifier} onChange={onChange} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="businessType">
                    Business Type <span className="text-danger">*</span>
                  </Label>
                  <Input id="businessType" name="businessType" type="select" value={form.businessType} onChange={onChange} required>
                    {BUSINESS_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="monetaryCurrency">Currency</Label>
                  <Input id="monetaryCurrency" name="monetaryCurrency" type="select" value={form.monetaryCurrency} onChange={onChange}>
                    {MONETARY_CURRENCIES.map(curr => (
                      <option key={curr.id} value={curr.id}>{curr.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <LocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={onLocationChange}
              required={true}
              errors={locationErrors}
            />
            <div className="d-flex mt-3">
              <Button color="secondary" className="me-2" type="button" onClick={() => history.push(`/organisations/${id}`)} disabled={submitting}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationEditComponent, {
  onRedirecting: () => <Loading />,
});
